import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, X, Upload, ShieldAlert, Package, FileUp, FileDown, CheckCircle2, AlertTriangle, Music, Layers } from 'lucide-react';
import api, { uploadImage, uploadMedia, mediaUrl, errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const BLANK = { purpose: '', purposes: [], category: '', categories: [], title: '', summary: '', overview: '', hero_image: '', instructions: '', preparation: '', stages: '', after_meditation: '', key_principle: '', video_url: '', video_poster: '', audio_url: '', music_url: '', references_text: '', time_of_day: '', recommended_time: '', durations: [], setup: [], space_notes: '', activity_level: '', setting: '', source: 'Based on meditation instructions described by Osho in The Orange Book', is_free: false, price: 3.99, discount_price: '', min_tier: '', disclaimer_text: '', is_published: true, sort_order: 0 };

const PURPOSE_PRESETS = ['Stress Relief', 'Anxiety Support', 'Depression Support', 'Better Sleep', 'Letting Go', 'Emotional Release', 'Energy & Vitality', 'Awareness & Witnessing', 'Mindfulness', 'Inner Stillness', 'Joy & Celebration', 'Grounding', 'Healing'];
const CATEGORY_OPTS = ['Active', 'Passive'];

const DURATION_OPTS = [1, 3, 5, 10, 15, 30, 45, 60, 120];
const SETUP_OPTS = [{ k: 'private_space', label: 'Private space' }, { k: 'public_place', label: 'Public place' }, { k: 'outdoors', label: 'Outdoors' }, { k: 'anywhere', label: 'Anywhere' }];
const TIME_OPTS = ['dawn', 'morning', 'afternoon', 'evening', 'night', 'anytime'];
const durationLabel = (m) => (m >= 60 ? `${m / 60} hr${m >= 120 ? 's' : ''}` : `${m} min`);

const parseRefs = (txt) => (txt || '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
  const [title, author, note, link] = l.split('|').map((s) => (s || '').trim());
  return { title, author: author || null, note: note || null, link: link || null };
});
const refsToText = (refs) => (refs || []).map((r) => [r.title, r.author, r.note, r.link].filter((x) => x != null).join(' | ')).join('\n');

const AdminMeditations = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showBundles, setShowBundles] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkCat, setShowBulkCat] = useState(false);

  const load = () => api.get('/admin/meditations').then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const exportJson = async () => {
    try {
      const r = await api.get('/admin/meditations/export');
      const blob = new Blob([JSON.stringify(r.data.items, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'meditations-export.json'; a.click();
      URL.revokeObjectURL(url);
      toast({ title: `Exported ${r.data.count} techniques` });
    } catch (e) { toast({ title: 'Export failed', description: errText(e) }); }
  };

  const [linkingAudio, setLinkingAudio] = useState(false);
  const linkOshoworldAudio = async () => {
    if (!window.confirm('Link verified oshoworld.com audio to matching meditations (Dynamic, Kundalini, Nadabrahma, Nataraj)? Existing music links are kept.')) return;
    setLinkingAudio(true);
    try {
      const r = await api.post('/admin/meditations/populate-oshoworld-audio');
      toast({ title: `Linked audio for ${r.data.updated_count} meditation(s)` });
      setLoading(true); load();
    } catch (e) { toast({ title: 'Failed', description: errText(e) }); }
    finally { setLinkingAudio(false); }
  };

  const [consolidating, setConsolidating] = useState(false);
  const consolidatePurposes = async () => {
    if (!window.confirm('Merge the many old purpose labels into the ~13 consolidated groups? This cleans up the filter chips. Safe to run multiple times.')) return;
    setConsolidating(true);
    try {
      const r = await api.post('/admin/meditations/consolidate-purposes');
      toast({ title: `Purposes consolidated`, description: `${r.data.techniques_updated} updated · now ${r.data.distinct_purposes.length} groups` });
      setLoading(true); load();
    } catch (e) { toast({ title: 'Failed', description: errText(e) }); }
    finally { setConsolidating(false); }
  };

  if (editing) return <Editor tech={editing} onDone={() => { setEditing(null); setLoading(true); load(); }} onCancel={() => setEditing(null)} />;

  const purposes = [...new Set(items.map((t) => t.purpose).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="admin-meditations">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-3xl text-burgundy">Meditation Techniques</h1>
        <div className="flex gap-2">
          <button onClick={exportJson} className="pill-btn-outline" data-testid="export-meditations-btn"><FileDown className="w-4 h-4" /> Export</button>
          <button onClick={() => setShowImport(true)} className="pill-btn-outline" data-testid="import-meditations-btn"><FileUp className="w-4 h-4" /> Import</button>
          <button onClick={() => setShowBundles(true)} className="pill-btn-outline" data-testid="manage-bundles-btn"><Package className="w-4 h-4" /> Bundles</button>
          <button onClick={() => setShowDisclaimer(true)} className="pill-btn-outline" data-testid="edit-disclaimer-btn"><ShieldAlert className="w-4 h-4" /> Disclaimer</button>
          <button onClick={consolidatePurposes} disabled={consolidating} className="pill-btn-outline" data-testid="consolidate-purposes-btn"><Package className="w-4 h-4" /> {consolidating ? 'Cleaning…' : 'Consolidate Purposes'}</button>
          <button onClick={linkOshoworldAudio} disabled={linkingAudio} className="pill-btn-outline" data-testid="link-oshoworld-audio-btn"><Music className="w-4 h-4" /> {linkingAudio ? 'Linking…' : 'Link Osho Audio'}</button>
          <button onClick={() => setShowBulkCat(true)} className="pill-btn-outline" data-testid="bulk-categorize-btn"><Layers className="w-4 h-4" /> Bulk Categorize</button>
          <button onClick={() => setEditing({ ...BLANK })} className="pill-btn" data-testid="new-meditation-btn"><Plus className="w-4 h-4" /> New Technique</button>
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-mahogany" /> : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id} className="flex items-center justify-between p-4 rounded-2xl bg-white border border-rose/30">
              <div>
                <div className="font-medium text-burgundy">{t.title} <span className="text-xs text-mahogany/50">· {t.purpose}</span></div>
                <div className="text-xs text-mahogany/60 mt-0.5">{t.is_free ? 'Free for members' : `Premium · $${t.discount_price ? `${t.discount_price} (was $${t.price})` : t.price}${t.min_tier ? ` · ${t.min_tier}+` : ''}`} · {t.is_published ? 'Published' : 'Draft'}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing({ ...t, references_text: refsToText(t.references), min_tier: t.min_tier || '', discount_price: t.discount_price ?? '', disclaimer_text: t.disclaimer_text || '', purposes: (t.purposes && t.purposes.length ? t.purposes : (t.purpose ? [t.purpose] : [])) })} className="p-2 rounded-lg hover:bg-rose-soft/40" title="Edit" data-testid={`edit-meditation-${t.id}`}><Pencil className="w-4 h-4 text-mahogany" /></button>
                <button onClick={async () => { if (window.confirm('Delete this technique?')) { await api.delete(`/admin/meditations/${t.id}`); toast({ title: 'Deleted' }); setLoading(true); load(); } }} className="p-2 rounded-lg hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </li>
          ))}
          {!items.length && <p className="text-mahogany/60">No techniques yet.</p>}
        </ul>
      )}
      {showDisclaimer && <DisclaimerModal onClose={() => setShowDisclaimer(false)} />}
      {showBundles && <BundlesModal purposes={purposes} onClose={() => setShowBundles(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); setLoading(true); load(); }} />}
      {showBulkCat && <BulkCategorizeModal items={items} onClose={() => setShowBulkCat(false)} onDone={() => { setShowBulkCat(false); setLoading(true); load(); }} />}
    </div>
  );
};

const Field = ({ label, ...p }) => (<label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">{label}</span><input {...p} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 focus:outline-none text-sm" /></label>);
const Area = ({ label, rows = 5, ...p }) => (<label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">{label}</span><textarea {...p} rows={rows} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 focus:outline-none text-sm" /></label>);

const MediaUpload = ({ label, value, accept, image, onChange, testid }) => {
  const ref = useRef();
  const [up, setUp] = useState(false);
  const { toast } = useToast();
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUp(true);
    try { const url = image ? await uploadImage(file) : await uploadMedia(file); onChange(url); toast({ title: 'Uploaded' }); }
    catch (err) { toast({ title: 'Upload failed', description: errText(err) }); }
    finally { setUp(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div>
      <div className="flex items-end gap-2">
        <div className="flex-1"><Field label={label} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Paste a URL or upload" data-testid={testid} /></div>
        <button type="button" onClick={() => ref.current?.click()} disabled={up} className="pill-btn-outline shrink-0 mb-0.5" data-testid={`${testid}-upload`}>{up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</button>
        <input ref={ref} type="file" accept={accept} onChange={pick} className="hidden" />
      </div>
      {value && image && <img src={mediaUrl(value)} alt="preview" className="mt-2 h-20 rounded-lg object-cover border border-rose/30" />}
      {value && accept?.startsWith('audio') && <audio controls src={mediaUrl(value)} className="mt-2 w-full h-9" />}
    </div>
  );
};

const DisclaimerModal = ({ onClose }) => {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/meditations/disclaimer').then((r) => setText(r.data.text || '')).finally(() => setLoading(false)); }, []);
  const save = async () => { setSaving(true); try { await api.put('/admin/meditations/disclaimer', { text }); toast({ title: 'Disclaimer saved' }); onClose(); } catch (e) { toast({ title: 'Save failed', description: errText(e) }); } finally { setSaving(false); } };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="disclaimer-modal">
      <div className="bg-cream rounded-3xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Global health &amp; safety disclaimer</h2><button onClick={onClose}><X className="w-5 h-5 text-mahogany/70" /></button></div>
        <p className="text-sm text-mahogany/70 mb-3">Shown to every member before they access a technique. A technique can override this with its own disclaimer.</p>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-mahogany" /> : (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="disclaimer-text" />
        )}
        <div className="flex gap-3 pt-4"><button onClick={save} disabled={saving} className="pill-btn" data-testid="save-disclaimer-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button><button onClick={onClose} className="pill-btn-outline">Cancel</button></div>
      </div>
    </div>
  );
};

const ImportModal = ({ onClose, onDone }) => {
  const { toast } = useToast();
  const fileRef = useRef();
  const [text, setText] = useState('');
  const [price, setPrice] = useState(3.99);
  const [onDup, setOnDup] = useState('update');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [parseError, setParseError] = useState('');

  const parseItems = () => {
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [data];
  };

  const run = async (dryRun) => {
    setParseError('');
    let items;
    try { items = parseItems(); } catch (e) { setParseError('Invalid JSON: ' + e.message); return; }
    if (!items.length) { setParseError('No techniques found in the JSON.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/admin/meditations/import', { items, dry_run: dryRun, default_price: Number(price) || 0, default_free: false, on_duplicate: onDup });
      setResult(r.data);
      if (!dryRun) {
        toast({ title: `Imported ${r.data.summary.to_create} new, ${r.data.summary.to_update} updated` });
        onDone();
      }
    } catch (e) { toast({ title: 'Import failed', description: errText(e) }); }
    finally { setBusy(false); }
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const t = await file.text(); setText(t); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const s = result?.summary;
  const canImport = s && s.dry_run && (s.to_create + s.to_update) > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="bg-cream rounded-3xl p-6 max-w-3xl w-full max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><FileUp className="w-5 h-5" /> Bulk import techniques</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-mahogany/70" /></button>
        </div>
        <p className="text-sm text-mahogany/70 mb-3">Paste a JSON array of techniques (your source schema is auto-converted), or upload a <code>.json</code> file. Click <strong>Preview</strong> first — nothing is saved until you confirm.</p>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">Default price (USD)</span>
            <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 w-32 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="import-price" /></label>
          <label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">If it already exists</span>
            <select value={onDup} onChange={(e) => setOnDup(e.target.value)} className="mt-1 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="import-ondup">
              <option value="update">Update it</option><option value="skip">Skip it</option>
            </select></label>
          <button type="button" onClick={() => fileRef.current?.click()} className="pill-btn-outline" data-testid="import-file-btn"><Upload className="w-4 h-4" /> Upload .json</button>
          <input ref={fileRef} type="file" accept=".json,application/json" onChange={pickFile} className="hidden" />
        </div>

        <textarea value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} rows={10} placeholder='[ { "name": "Gibberish Meditation", "purposes": ["Letting Go"], "short_excerpt": "...", "technique_details": "...", ... } ]'
          className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm font-mono" data-testid="import-textarea" />
        {parseError && <p className="mt-2 text-sm text-red-600" data-testid="import-parse-error">{parseError}</p>}

        {s && (
          <div className="mt-4 rounded-2xl border border-rose/40 bg-white p-4" data-testid="import-summary">
            <p className="text-sm text-burgundy font-medium mb-2">
              {s.dry_run ? 'Preview' : 'Imported'}: {s.total} total · {s.to_create} new · {s.to_update} update{s.skipped ? ` · ${s.skipped} skipped` : ''}{s.errors ? ` · ${s.errors} error(s)` : ''}
            </p>
            <ul className="space-y-1 max-h-60 overflow-y-auto text-sm">
              {result.results.map((r, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`import-row-${i}`}>
                  {r.action === 'error' ? <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />}
                  <div>
                    <span className="text-mahogany">{r.title || `Item ${r.index + 1}`}</span>
                    <span className="text-xs text-mahogany/50"> · {r.action}</span>
                    {r.error && <span className="text-xs text-red-600"> — {r.error}</span>}
                    {(r.warnings || []).map((w, j) => <div key={j} className="text-xs text-amber-700">⚠ {w}</div>)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <button onClick={() => run(true)} disabled={busy || !text.trim()} className="pill-btn-outline" data-testid="import-preview-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Preview</button>
          <button onClick={() => run(false)} disabled={busy || !canImport} className="pill-btn" data-testid="import-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Import {canImport ? s.to_create + s.to_update : ''}</button>
          <button onClick={onClose} className="pill-btn-outline">Close</button>
        </div>
      </div>
    </div>
  );
};

const B_BLANK = { title: '', purpose: '', description: '', price: 9.99, discount_price: '', is_published: true, sort_order: 0 };

const BundlesModal = ({ purposes, onClose }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = () => api.get('/admin/meditations/bundles/all').then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.title || !f.purpose) return toast({ title: 'Title and purpose are required' });
    setSaving(true);
    const payload = { ...f, price: parseFloat(f.price) || 0, discount_price: f.discount_price === '' || f.discount_price == null ? null : parseFloat(f.discount_price), sort_order: parseInt(f.sort_order, 10) || 0 };
    try {
      if (f.id) await api.patch(`/admin/meditations/bundles/${f.id}`, payload); else await api.post('/admin/meditations/bundles', payload);
      toast({ title: 'Saved' }); setF(null); setLoading(true); load();
    } catch (e) { toast({ title: 'Save failed', description: errText(e) }); } finally { setSaving(false); }
  };
  const remove = async (b) => { if (!window.confirm('Delete this bundle?')) return; await api.delete(`/admin/meditations/bundles/${b.id}`); toast({ title: 'Deleted' }); setLoading(true); load(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bundles-modal">
      <div className="bg-cream rounded-3xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><Package className="w-5 h-5" /> Meditation bundles</h2><button onClick={onClose}><X className="w-5 h-5 text-mahogany/70" /></button></div>
        <p className="text-sm text-mahogany/70 mb-4">A bundle unlocks every published technique in a purpose group for one discounted price.</p>

        {f ? (
          <div className="space-y-3 bg-white rounded-2xl border border-rose/30 p-4">
            <Field label="Title" value={f.title} onChange={(e) => set('title', e.target.value)} data-testid="bundle-title" />
            <label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">Purpose group</span>
              <input list="bundle-purposes" value={f.purpose} onChange={(e) => set('purpose', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="bundle-purpose" />
              <datalist id="bundle-purposes">{purposes.map((p) => <option key={p} value={p} />)}</datalist>
            </label>
            <Area label="Description" rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Price (USD)" type="number" step="0.01" value={f.price} onChange={(e) => set('price', e.target.value)} data-testid="bundle-price" />
              <Field label="Discounted price" type="number" step="0.01" value={f.discount_price} onChange={(e) => set('discount_price', e.target.value)} data-testid="bundle-discount" />
              <Field label="Sort order" type="number" value={f.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-mahogany"><input type="checkbox" checked={f.is_published} onChange={(e) => set('is_published', e.target.checked)} /> Published</label>
            <div className="flex gap-3"><button onClick={save} disabled={saving} className="pill-btn" data-testid="save-bundle-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button><button onClick={() => setF(null)} className="pill-btn-outline">Cancel</button></div>
          </div>
        ) : (
          <>
            <button onClick={() => setF({ ...B_BLANK })} className="pill-btn mb-4" data-testid="new-bundle-btn"><Plus className="w-4 h-4" /> New bundle</button>
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-mahogany" /> : (
              <ul className="space-y-2">
                {items.map((b) => (
                  <li key={b.id} className="flex items-center justify-between p-3 rounded-xl bg-white border border-rose/30">
                    <div><div className="font-medium text-burgundy">{b.title} <span className="text-xs text-mahogany/50">· {b.purpose}</span></div><div className="text-xs text-mahogany/60">${b.discount_price ? `${b.discount_price} (was $${b.price})` : b.price} · {b.is_published ? 'Published' : 'Draft'}</div></div>
                    <div className="flex gap-1"><button onClick={() => setF({ ...b, discount_price: b.discount_price ?? '' })} className="p-2 rounded-lg hover:bg-rose-soft/40" data-testid={`edit-bundle-${b.id}`}><Pencil className="w-4 h-4 text-mahogany" /></button><button onClick={() => remove(b)} className="p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button></div>
                  </li>
                ))}
                {!items.length && <p className="text-mahogany/60 text-sm">No bundles yet.</p>}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Editor = ({ tech, onDone, onCancel }) => {
  const { toast } = useToast();
  const [f, setF] = useState(() => {
    const raw = (tech.category || (tech.categories && tech.categories[0]) || '').trim().toLowerCase();
    const category = raw === 'active' ? 'Active' : raw === 'passive' ? 'Passive' : '';
    return { ...tech, category, categories: category ? [category] : [] };
  });
  const [saving, setSaving] = useState(false);
  const [purposeInput, setPurposeInput] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCategory = (c) => setF((p) => ({ ...p, category: c, categories: [c] }));
  const addPurpose = (val) => {
    const v = (val || '').trim();
    if (!v) return;
    if (!(f.purposes || []).some((p) => p.toLowerCase() === v.toLowerCase())) set('purposes', [...(f.purposes || []), v]);
    setPurposeInput('');
  };
  const removePurpose = (v) => set('purposes', (f.purposes || []).filter((p) => p !== v));
  const save = async () => {
    if (!f.title || !(f.purposes && f.purposes.length)) return toast({ title: 'Add at least one purpose and a title' });
    if (f.category !== 'Active' && f.category !== 'Passive') return toast({ title: 'Please choose a category — Active or Passive' });
    setSaving(true);
    const payload = {
      ...f, price: parseFloat(f.price) || 0,
      discount_price: f.discount_price === '' || f.discount_price == null ? null : parseFloat(f.discount_price),
      sort_order: parseInt(f.sort_order, 10) || 0, min_tier: f.min_tier || null,
      disclaimer_text: f.disclaimer_text || null,
      references: parseRefs(f.references_text),
    };
    delete payload.references_text;
    try {
      if (f.id) await api.patch(`/admin/meditations/${f.id}`, payload); else await api.post('/admin/meditations', payload);
      toast({ title: 'Saved' }); onDone();
    } catch (e) { toast({ title: 'Save failed', description: errText(e) }); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="meditation-editor">
      <div className="flex items-center justify-between"><h1 className="font-serif-display text-3xl text-burgundy">{f.id ? 'Edit' : 'New'} Technique</h1><button onClick={onCancel}><X className="w-5 h-5 text-mahogany/70" /></button></div>
      <div>
        <span className="text-xs uppercase tracking-widest text-mahogany/70">Purposes (a technique can serve several)</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="med-purposes">
          {(f.purposes || []).map((p) => (
            <span key={p} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-mahogany text-cream text-sm">{p}
              <button type="button" onClick={() => removePurpose(p)} className="hover:opacity-70"><X className="w-3 h-3" /></button></span>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <input value={purposeInput} onChange={(e) => setPurposeInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPurpose(purposeInput); } }}
            placeholder="Type a purpose (e.g. Sleep) and press Enter" className="flex-1 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="med-purpose-input" />
          <button type="button" onClick={() => addPurpose(purposeInput)} className="pill-btn-outline" data-testid="med-purpose-add">Add</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {PURPOSE_PRESETS.filter((p) => !(f.purposes || []).some((x) => x.toLowerCase() === p.toLowerCase())).map((p) => (
            <button type="button" key={p} onClick={() => addPurpose(p)} className="px-2.5 py-1 rounded-full text-xs bg-rose-soft/60 text-mahogany hover:bg-rose-soft">+ {p}</button>
          ))}
        </div>
      </div>
      <Field label="Title" value={f.title} onChange={(e) => set('title', e.target.value)} data-testid="med-title" />
      <div data-testid="med-categories-field">
        <span className="text-xs uppercase tracking-widest text-mahogany/70">Category <span className="text-rose-italic">*</span></span>
        <div className="mt-1.5 flex gap-2" data-testid="med-categories">
          {CATEGORY_OPTS.map((c) => {
            const on = f.category === c;
            return (
              <button type="button" key={c} onClick={() => setCategory(c)}
                className={`px-5 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50 hover:bg-rose-soft/40'}`}
                data-testid={`med-category-${c.toLowerCase()}`}>{c}</button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-mahogany/55">Active techniques involve movement, breath or catharsis · Passive techniques are still, silent or witnessing.</p>
      </div>
      <label className="block max-w-xs"><span className="text-xs uppercase tracking-widest text-mahogany/70">Time of day</span>
        <select value={f.time_of_day} onChange={(e) => set('time_of_day', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm capitalize" data-testid="med-timeofday">
          <option value="">Any time</option>{TIME_OPTS.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
        </select></label>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Recommended time" value={f.recommended_time} onChange={(e) => set('recommended_time', e.target.value)} placeholder="e.g. Before sunrise, empty stomach" data-testid="med-recommended-time" />
        <Field label="Activity level" value={f.activity_level} onChange={(e) => set('activity_level', e.target.value)} placeholder="e.g. Active / Gentle / Passive" data-testid="med-activity-level" />
        <Field label="Setting" value={f.setting} onChange={(e) => set('setting', e.target.value)} placeholder="e.g. Dim light, no interruptions" data-testid="med-setting" />
      </div>
      <div>
        <span className="text-xs uppercase tracking-widest text-mahogany/70">Duration options (minutes)</span>
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="med-durations">
          {DURATION_OPTS.map((m) => {
            const on = (f.durations || []).includes(m);
            return <button type="button" key={m} onClick={() => set('durations', on ? f.durations.filter((x) => x !== m) : [...(f.durations || []), m])}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`}>{durationLabel(m)}</button>;
          })}
        </div>
      </div>
      <div>
        <span className="text-xs uppercase tracking-widest text-mahogany/70">Where can it be practised?</span>
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="med-setup">
          {SETUP_OPTS.map((s) => {
            const on = (f.setup || []).includes(s.k);
            return <button type="button" key={s.k} onClick={() => set('setup', on ? f.setup.filter((x) => x !== s.k) : [...(f.setup || []), s.k])}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`}>{s.label}</button>;
          })}
        </div>
      </div>
      <Field label="Space notes (optional)" value={f.space_notes} onChange={(e) => set('space_notes', e.target.value)} placeholder="e.g. Enough room to move freely" data-testid="med-space-notes" />
      <Field label="Excerpt (short summary)" value={f.summary} onChange={(e) => set('summary', e.target.value)} data-testid="med-summary" />
      <Area label="Overview" rows={3} value={f.overview} onChange={(e) => set('overview', e.target.value)} data-testid="med-overview" />
      <MediaUpload label="Hero image" value={f.hero_image} accept="image/*,.heic,.heif" image onChange={(v) => set('hero_image', v)} testid="med-hero" />
      <Area label="Preparation" rows={3} value={f.preparation} onChange={(e) => set('preparation', e.target.value)} data-testid="med-preparation" />
      <Area label="Stages" rows={5} value={f.stages} onChange={(e) => set('stages', e.target.value)} data-testid="med-stages" />
      <Area label="Instructions (step by step)" value={f.instructions} onChange={(e) => set('instructions', e.target.value)} />
      <Area label="After meditation" rows={3} value={f.after_meditation} onChange={(e) => set('after_meditation', e.target.value)} data-testid="med-after" />
      <Field label="Key principle" value={f.key_principle} onChange={(e) => set('key_principle', e.target.value)} placeholder="The core idea in one line" data-testid="med-key-principle" />
      <MediaUpload label="Video URL (YouTube or file)" value={f.video_url} accept="video/*" onChange={(v) => set('video_url', v)} testid="med-video" />
      <MediaUpload label="Video cover image (thumbnail shown before the video plays)" value={f.video_poster} accept="image/*,.heic,.heif" image onChange={(v) => set('video_poster', v)} testid="med-video-poster" />
      <MediaUpload label="Guided audio (upload mp3 or paste URL)" value={f.audio_url} accept="audio/*" onChange={(v) => set('audio_url', v)} testid="med-audio" />
      <Field label="Music URL" value={f.music_url} onChange={(e) => set('music_url', e.target.value)} />
      <Field label="Source (origin / tradition / attribution)" value={f.source} onChange={(e) => set('source', e.target.value)} placeholder="e.g. Osho Active Meditations" data-testid="med-source" />
      <Area label="References (one per line: Title | Author | Note | Link)" value={f.references_text} onChange={(e) => set('references_text', e.target.value)} />
      <Area label="Disclaimer override (leave blank to use the global disclaimer)" rows={4} value={f.disclaimer_text} onChange={(e) => set('disclaimer_text', e.target.value)} data-testid="med-disclaimer" />
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <Field label="Price (USD)" type="number" step="0.01" value={f.price} onChange={(e) => set('price', e.target.value)} data-testid="med-price" />
        <Field label="Discounted price" type="number" step="0.01" value={f.discount_price} onChange={(e) => set('discount_price', e.target.value)} placeholder="e.g. 1.99" data-testid="med-discount" />
        <label className="block"><span className="text-xs uppercase tracking-widest text-mahogany/70">Unlock tier</span>
          <select value={f.min_tier} onChange={(e) => set('min_tier', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm">
            <option value="">Any premium tier</option><option value="meditators">Meditators+</option><option value="gold">Gold+</option><option value="diamond">Diamond+</option><option value="lifetime">Lifetime</option>
          </select></label>
        <Field label="Sort order" type="number" value={f.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm text-mahogany"><input type="checkbox" checked={f.is_free} onChange={(e) => set('is_free', e.target.checked)} /> Free for members</label>
        <label className="flex items-center gap-2 text-sm text-mahogany"><input type="checkbox" checked={f.is_published} onChange={(e) => set('is_published', e.target.checked)} /> Published</label>
      </div>
      <div className="flex gap-3 pt-2"><button onClick={save} disabled={saving} className="pill-btn" data-testid="save-meditation-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button><button onClick={onCancel} className="pill-btn-outline">Cancel</button></div>
    </div>
  );
};

const normCat = (c) => { const v = (c || '').trim().toLowerCase(); return v === 'active' ? 'Active' : v === 'passive' ? 'Passive' : ''; };

const BulkCategorizeModal = ({ items, onClose, onDone }) => {
  const { toast } = useToast();
  const [map, setMap] = useState(() => Object.fromEntries(items.map((t) => [t.id, normCat(t.category || (t.categories && t.categories[0]))])));
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const setCat = (id, c) => setMap((m) => ({ ...m, [id]: c }));
  const setAllRemaining = (c) => setMap((m) => { const n = { ...m }; items.forEach((t) => { if (!n[t.id]) n[t.id] = c; }); return n; });
  const remaining = items.filter((t) => !map[t.id]).length;
  const filtered = items.filter((t) => (t.title || '').toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    const updates = items.filter((t) => map[t.id] === 'Active' || map[t.id] === 'Passive').map((t) => ({ id: t.id, category: map[t.id] }));
    if (!updates.length) return toast({ title: 'Nothing to save — tag at least one technique' });
    setSaving(true);
    try {
      const r = await api.post('/admin/meditations/bulk-category', { updates });
      toast({ title: `Updated ${r.data.updated} technique(s)` });
      onDone();
    } catch (e) { toast({ title: 'Failed', description: errText(e) }); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-category-modal">
      <div className="bg-cream rounded-3xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="p-5 border-b border-rose/30 flex items-center justify-between">
          <div>
            <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><Layers className="w-5 h-5" /> Bulk categorize</h2>
            <p className="text-xs text-mahogany/60 mt-0.5" data-testid="bulk-remaining-count">{remaining} of {items.length} still unset · tag each as Active or Passive</p>
          </div>
          <button onClick={onClose} data-testid="bulk-category-close"><X className="w-5 h-5 text-mahogany/70" /></button>
        </div>
        <div className="p-4 border-b border-rose/20 flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search techniques…" className="flex-1 min-w-[180px] bg-white rounded-full border border-rose/50 px-4 py-2 text-sm focus:outline-none" data-testid="bulk-category-search" />
          <button type="button" onClick={() => setAllRemaining('Active')} className="pill-btn-outline text-xs" data-testid="bulk-set-remaining-active">Set remaining Active</button>
          <button type="button" onClick={() => setAllRemaining('Passive')} className="pill-btn-outline text-xs" data-testid="bulk-set-remaining-passive">Set remaining Passive</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {filtered.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-white border border-rose/20" data-testid={`bulk-row-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-burgundy truncate">{t.title}</div>
                <div className="text-[11px] text-mahogany/50 truncate">{t.purpose}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {CATEGORY_OPTS.map((c) => {
                  const on = map[t.id] === c;
                  return (
                    <button key={c} type="button" onClick={() => setCat(t.id, c)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50 hover:bg-rose-soft/40'}`}
                      data-testid={`bulk-${c.toLowerCase()}-${t.id}`}>{c}</button>
                  );
                })}
              </div>
            </div>
          ))}
          {!filtered.length && <p className="text-sm text-mahogany/50 text-center py-6">No techniques match your search.</p>}
        </div>
        <div className="p-4 border-t border-rose/30 flex items-center justify-end gap-2">
          <button onClick={onClose} className="pill-btn-outline" data-testid="bulk-category-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="pill-btn" data-testid="bulk-category-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save all</button>
        </div>
      </div>
    </div>
  );
};


export default AdminMeditations;
